// @flow
import * as Sentry from '@sentry/react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import type { StripeContext } from 'components/ResourceFill/types/stripe-context'
import flatMapDeep from 'lodash/flatMapDeep'
import * as React from 'react'
import { applyAnswerToResponse } from 'utils/answers'
import { ZentakeContext } from 'utils/ZentakeContext'

import { useAddStripeCustomerIntegration } from '@/queries/integrations/stripe/useAddStripeCustomerIntegration'

type Props = {
  children: React.Node,
}

export const ResourceStripeContext: React.Context<?StripeContext> =
  React.createContext(null)

function ResourceStripeProvider({ children }: Props): React.Node {
  const { setStripeIntegration } = React.useContext(ZentakeContext)
  const { addStripeCustomerIntegration } = useAddStripeCustomerIntegration()

  const { response } = React.useContext(ResourceResponseContext) || {
    response: {
      answers: [],
      created_at: '',
      id: '',
      internal_user: null,
      is_archived: false,
      is_scheduled: false,
      is_submitted: false,
      name: '',
      uid: '',
    },
  }

  const [stripeEmpty, setStripeEmpty] = React.useState(true)
  const [stripeError, setStripeError] = React.useState(null)

  const stripe = useStripe()
  const elements = useElements()

  React.useEffect(() => {
    if (response?.team_details?.stripe_integration?.account_id) {
      setStripeIntegration(response.team_details?.stripe_integration)
    }
  }, [response?.team_details?.stripe_integration, setStripeIntegration])

  const getCreditCardQuestion = React.useCallback(() => {
    const form = response?.form

    if (!form) return

    const questions = flatMapDeep(
      form?.pages?.map((p) =>
        p?.sections?.map((s) => s?.rows?.map((r) => r?.questions))
      )
    )
    return questions?.find(
      (q) => q?.question_type?.question_type === 'credit_card'
    )
  }, [response?.form])

  const isCreditCardRequired = React.useCallback(() => {
    const creditCardQuestion = getCreditCardQuestion?.()

    return creditCardQuestion?.is_required
  }, [getCreditCardQuestion])

  const submitStripeData = React.useCallback(async () => {
    if (!stripe || !elements || !response) return null

    const isRequired = isCreditCardRequired()

    const cardElement = elements.getElement('card')

    if (!cardElement) return null

    const paymentMethodData = {
      type: 'card',
      card: cardElement,
    }
    const result = await stripe.createPaymentMethod(paymentMethodData)

    if (result.error) {
      if (
        result.error.message === 'Your card number is incomplete.' &&
        !isRequired
      ) {
        setStripeError(null)
        return null
      }

      setStripeError(result.error.message)
      Sentry.captureException(result.error)
      throw new Error(result.error.message)
    }

    const paymentMethodId = result.paymentMethod.id

    try {
      await addStripeCustomerIntegration({
        payment_method_id: paymentMethodId,
        response_id: response.id,
      })
    } catch (e) {
      Sentry.captureException(e)
      throw e
    }

    return paymentMethodId
  }, [
    stripe,
    elements,
    response,
    isCreditCardRequired,
    addStripeCustomerIntegration,
  ])

  const handleCreditCardAnswerChange = React.useCallback(
    (answer) => {
      const question = getCreditCardQuestion()

      if (!question) return [response, null]

      const updatedResponse = applyAnswerToResponse(response, question, answer)

      return [updatedResponse, question.id]
    },
    [getCreditCardQuestion, response]
  )

  const value = React.useMemo(
    () => ({
      stripeEmpty,
      setStripeEmpty,
      stripeError,
      setStripeError,
      submitStripeData,
      handleCreditCardAnswerChange,
    }),
    [handleCreditCardAnswerChange, stripeEmpty, stripeError, submitStripeData]
  )

  return (
    <ResourceStripeContext.Provider value={value}>
      {children}
    </ResourceStripeContext.Provider>
  )
}

export default ResourceStripeProvider
